import { useQuery } from "@apollo/client";
import { Heading, Text } from "@nbs/ui";
import { STORY_COMMUNITY_PERCEPTIONS_QUERY, STORY_SURVEY_INDICATOR_IDS } from "../api/documents";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsHorizontalBarChart } from "@/features/charts/NbsHorizontalBarChart";
import { STORY_IMAGES, StoryMedia } from "./StoryMedia";

type StoryCommunityStepProps = {
  readonly onBack: () => void;
  readonly onContinue: () => void;
};

type Dist = {
  indicateurId: string;
  libelle: string;
  sampleSize?: number | null;
  territoryLabel?: string | null;
  buckets: Array<{ modalityLabel: string; value: number; unit: string }>;
} | null;

function chartFrom(dist: Dist) {
  return (dist?.buckets ?? []).map((b) => ({ label: b.modalityLabel, value: b.value }));
}

const CHARTS: readonly {
  key: "erosion" | "soils" | "water" | "forest" | "climate" | "satisfaction";
  title: string;
  theme: string;
}[] = [
  {
    key: "erosion",
    title: "signes d’érosion",
    theme: "Sols & dégradation",
  },
  {
    key: "soils",
    title: "réduction de la productivité des sols",
    theme: "Sols & dégradation",
  },
  {
    key: "water",
    title: "Disponibilité des ressources en eau",
    theme: "Eau",
  },
  {
    key: "forest",
    title: "réduction de la surface de la forêt",
    theme: "Forêt",
  },
  {
    key: "climate",
    title: "Appréciation du climat de la localité",
    theme: "Climat",
  },
  {
    key: "satisfaction",
    title: "Niveau de satisfaction des services que la nature offre",
    theme: "Services de la nature",
  },
];

export function StoryCommunityStep({ onBack, onContinue }: StoryCommunityStepProps) {
  const { data, loading, error, refetch } = useQuery(STORY_COMMUNITY_PERCEPTIONS_QUERY, {
    variables: {
      territoryIds: null,
      dateFrom: "2005-01-01",
      dateTo: "2025-12-31",
      ...STORY_SURVEY_INDICATOR_IDS,
    },
    fetchPolicy: "cache-and-network",
  });

  return (
    <section
      className="nbs-story-step nbs-story-dash"
      data-testid="story-community-step"
      aria-labelledby="story-community-heading"
    >
      <header className="nbs-story-dash__intro">
        <div className="nbs-story-dash__intro-title">
          <p className="nbs-story-step__eyebrow">Étape 5 · Perceptions</p>
          <Heading level={1} id="story-community-heading">
            Que nous disent les communautés ?
          </Heading>
          <p className="nbs-story-step__question">
            Comment les populations perçoivent-elles ces changements ?
          </p>
        </div>
        <div className="nbs-story-dash__intro-aside">
          <StoryMedia
            src={STORY_IMAGES.nature}
            alt="Récolte communautaire de piments, photo CECI"
            caption="Territoires habités — perceptions communautaires"
            ratio="card"
          />
          <Text className="nbs-story-step__lead">
            Érosion, sols, eau, forêt, climat et satisfaction — six lectures d’enquête pour croiser
            le vécu local avec les tendances d’occupation des sols.
          </Text>
          <Text className="nbs-story-step__lead" data-testid="story-community-sample">
            L’enquête sur les perceptions a recueilli les réponses de 72 personnes, dont 28 femmes
            (39 %).
          </Text>
        </div>
      </header>

      {error ? (
        <div className="nbs-story-dash__error" role="alert">
          <Text>Impossible de charger les distributions d’enquête.</Text>
          <button type="button" className="nbs-story-step__cta" onClick={() => void refetch()}>
            Réessayer
          </button>
        </div>
      ) : null}

      <div className="nbs-story-dash__charts" data-testid="story-community-charts">
        {CHARTS.map((chart) => {
          const dist = (data?.[chart.key] ?? null) as Dist;
          const rows = chartFrom(dist);
          return (
            <NbsChartContainer
              key={chart.key}
              title={chart.title}
              description={chart.theme}
              unit="%"
              territory={dist?.territoryLabel ?? null}
              source={`Enquête · n=${dist?.sampleSize ?? "—"}`}
              isLoading={loading && !dist}
              empty={!loading && rows.length === 0}
              emptyMessage="Pas encore de distribution publiée pour cet indicateur."
            >
              <NbsHorizontalBarChart data={rows} height={340} />
            </NbsChartContainer>
          );
        })}
      </div>

      <div className="nbs-story-step__actions">
        <button
          type="button"
          className="nbs-story-step__cta nbs-story-step__cta--ghost"
          onClick={onBack}
        >
          Retour : changements des paysages
        </button>
        <button type="button" className="nbs-story-step__cta" onClick={onContinue}>
          Suite : services des écosystèmes
        </button>
      </div>
    </section>
  );
}
